import Store from '@/store/index';
import { mode, partyTypeEnum, requestReplyTypes, attachmentType, attachmentCategory, similarCaseTypes, casePartyRoleEnum, socialIdentityTypeEnum, countriesEnum } from '@/helpers/enum.js';
import ar from '@/local/ar';
import { deepClone, resetScroll, uuidGenerator } from '@/helpers/utils';
import Vue from '@/main';
import router from '@/routes';
import { makeCancellable, takeLatest } from 'vuex-cancellable-actions';
import {
  convertCasePartyToCasePartyRepresentative,
  getCasePartyByObjectKey,
  getCasePartyOrRepresentativeByObjectKey,
  getRelatedCasePartyByObjectKey,
  getRepresentativeByRelatedObjectKey,
  getRepresentativeObjectKeyByRelatedObjectKey,
  isCasePartyExists,
  isProsecutorOrDefendant,
} from '../helpers/utils';
import api from '../services/api';
import casesApi from '../../cases/services/api';
import services from '../services/services';
import { initialData } from './state';

export default makeCancellable({
  currentStepChanged({ commit }, step) {
    commit('updateCurrentStep', step);
  },
  openLawsuit({ commit, state, dispatch }, { currentStep, objectKey }) {
    commit('updateCurrentStep', objectKey ? currentStep : initialData().currentStep);
    commit('updateIsPledged', initialData().isPledged);
    commit('resetCasePartyForm');
    commit('resetLawsuitRequest');
    commit('updateActivePartyAttributes', []);
    commit('updateSubmittedRequestInfo', initialData().submittedRequestInfo);
    if (objectKey) {
      commit('updateSubmittedRequestInfoObjectKey', objectKey);
      commit('updateIsPledged', true);
      router.push({ path: `/lawsuit-requests/${objectKey}/${+state.currentStep + 1}` });
      api.getLawsuitRequestInfo(objectKey)
        .then((res) => {
          commit('updateSubmittedRequestInfo', {
            ...res.data,
            objectKey,
          });
          api.getCaseMovementReplyTypes(objectKey)
            .then((actions) => {
              commit('updateCaseMovementReplyTypes', actions.data);
              return actions.data;
            });
          return res;
        });
      getLawsuitCaseInfo({
        commit,
        dispatch,
      }, objectKey);
      api.getLawsuitCaseParties(objectKey)
        .then((res) => {
          return services
            .refineLawsuitCaseParties(res.data)
            .then((caseParties) => {
              commit('updateCaseParties', caseParties);
              return caseParties;
            })
            .catch(() => { });
        });
      api.getPortalCasePreviousReplies(objectKey)
        .then((res) => {
          commit('updateCasePreviousReplies', res.data);
          return res;
        });
      dispatch('updateSimilarCasesModalVisibility', { visibility: false });
      dispatch('updateReplyModalVisibility', { visibility: false });
      dispatch('getAuditFields', objectKey);
      dispatch('getLawsuitCaseAttachment', objectKey);
    }
  },
  openLawsuitPreviousReplies({ commit }, objectKey) {
    commit('updateIsPledged', initialData().isPledged);
    commit('resetLawsuitRequest');
    commit('updateSubmittedRequestInfo', initialData().submittedRequestInfo);
    if (objectKey) {
      commit('updateIsPledged', true);
      commit('updateSubmittedRequestInfoObjectKey', objectKey);
      return Promise.all([
        api.getLawsuitRequestInfo(objectKey),
        api.getPortalCasePreviousReplies(objectKey),
      ]).then((res) => {
        commit('updateSubmittedRequestInfo', {
          ...res[0].data,
          objectKey,
        });
        commit('updateCasePreviousReplies', res[1].data);
        return objectKey;
      });
    }
    return Promise.reject();
  },
  openCaseRelatedPreviousReplies({ commit }, objectKey) {
    commit('updateIsPledged', initialData().isPledged);
    commit('resetLawsuitRequest');
    commit('updateSubmittedRequestInfo', initialData().submittedRequestInfo);
    if (objectKey) {
      commit('updateIsPledged', true);
      commit('updateSubmittedRequestInfoObjectKey', objectKey);
      return Promise.all([
        casesApi.getCaseRelatedRequests(objectKey),
        api.getCasePreviousReplies(objectKey),
      ]).then((res) => {
        commit('updateSubmittedRequestInfo', {
          ...res[0].data,
          objectKey,
        });
        commit('updateCasePreviousReplies', res[1].data);
        return objectKey;
      });
    }
    return Promise.reject();
  },
  stepPledgeNext({ commit, state }) {
    const nextStep = state.currentStep + 1;
    commit('updateCurrentStep', nextStep);
  },
  stepCaseCategoryNext({ commit, state, getters }, payload) {
    const nextStep = state.currentStep + 1;
    const caseData = {
      ...state.lawsuitRequest.case,
      ...payload,
    };
    services
      .validateLawsuitCaseCategory(caseData)
      .then(async () => {
        commit('updateCase', caseData);
        commit('updateActivePartyAttributes', []);
        commit('updateCurrentStep', nextStep);
        if (getters.requestObjectKey) {
          router.push({ path: `/lawsuit-requests/${getters.requestObjectKey}/${+nextStep + 1}` });
        }
      })
      .catch(Vue.toaster.error);
  },
  stepCasePartiesNext({ commit, state, getters }) {
    const nextStep = state.currentStep + 1;
    services
      .validateLawsuit()
      .then(() => {
        commit('updateCurrentStep', nextStep);
        if (getters.requestObjectKey) {
          router.push({
            path: `/lawsuit-requests/${getters.requestObjectKey}/${+nextStep +
              1}`,
          });
        }
      })
      .catch(Vue.toaster.error);
  },
  stepCaseSubjectNext({ commit, getters, state }) {
    const caseData = state.lawsuitRequest.case;
    const nextStep = state.currentStep + 1;
    services
      .validateLawsuitCaseSubject(caseData)
      .then(async () => {
        if (getters.isEditMode && caseData.caseHasChanges) {
          await api
            .getCaseMovementReplyTypes(getters.requestObjectKey)
            .then((res) => {
              commit('updateCaseMovementReplyTypes', res.data);
              return res.data;
            });

          api
            .updateLawsuitCase(getters.requestObjectKey, caseData)
            .then((res) => {
              commit('updateCase', {
                ...caseData,
                caseHasChanges: false,
              });
              commit('updateCurrentStep', nextStep);
              router.push({ path: `/lawsuit-requests/${getters.requestObjectKey}/${+nextStep + 1}` });
              return res;
            });
        } else if (getters.isNewMode) {
          await api.submitLawsuit(getters.lawsuitRequest)
            .then((res) => {
              commit('updateSubmittedRequestInfo', {
                ...res.data,
                mode: mode.update,
              });

              api
                .getCaseMovementReplyTypes(getters.requestObjectKey)
                .then((res) => {
                  commit('updateCaseMovementReplyTypes', res.data);
                  return res.data;
                });

              commit('updateCase', caseData);
              commit('updateCurrentStep', nextStep);
              if (getters.requestObjectKey) {
                router.push({ path: `/lawsuit-requests/${getters.requestObjectKey}/${+nextStep + 1}` });
              }

              Vue.toaster.success('لقد تم انشاء طلب صحيفة الدعوى بنجاح');

              api.getLawsuitCaseParties(res.data.objectKey)
                .then((res) => {
                  return services
                    .refineLawsuitCaseParties(res.data)
                    .then((caseParties) => {
                      commit('updateCaseParties', caseParties);
                      return caseParties;
                    })
                    .catch(() => { });
                });

              return res;
            })
            .catch(() => { });
        } else {
          commit('updateCurrentStep', nextStep);
          router.push({ path: `/lawsuit-requests/${getters.requestObjectKey}/${+nextStep + 1}` });
        }
      })
      .catch(Vue.toaster.error);
  },
  saveDraftLawsuit({ getters }) {
    return new Promise((resolve) => {
      services
        .validateDraftLawsuit()
        .then(async () => {
          api.submitLawsuit({
            ...getters.lawsuitRequest,
            isTempSave: true,
          }).then((res) => {
            Vue.toaster.success('لقد تم حفظ طلب صحيفة الدعوى كمسودة بنجاح');

            resolve(res);
            return res;
          });
        })
        .catch(Vue.toaster.error);
    });
  },
  stepBack({ commit, state, getters }) {
    const nextStep = state.currentStep - 1;
    commit('updateCurrentStep', nextStep);
    if (getters.requestObjectKey) {
      router.push({ path: `/lawsuit-requests/${getters.requestObjectKey}/${+nextStep + 1}` });
    }
  },
  setRequestApplicant({ commit, state, getters }, casePartyObjectKey) {
    if (getters.isEditMode && casePartyObjectKey) {
      api.changeRequestApplicant(getters.requestObjectKey, casePartyObjectKey);
    }

    const caseParties = deepClone(state.lawsuitRequest.caseParties);

    caseParties.forEach((cp) => {
      // eslint-disable-next-line
      cp.isRequestApplicant =
        (casePartyObjectKey && cp.objectKey === casePartyObjectKey) ||
        cp.relatedPartiesInfo.some(
          rp => casePartyObjectKey && rp.objectKey === casePartyObjectKey
        );
    });

    commit('updateCaseParties', caseParties);
  },
  getCourtAndSubCircleByJudgementCode(_context, judgmentNumber) {
    return api.getCourtAndSubCircleByJudgementCode(judgmentNumber).then(res => res.data);
  },
  openAddCaseParty({ commit }, obj) {
    commit('updateSubMode', 'add');
    const caseParty = initialData().selectedCaseParty;
    caseParty.objectKey = uuidGenerator();
    caseParty.casePartyRole = obj.casePartyRole;

    // fix scroll behavior
    resetScroll();

    commit('updateSelectedCaseParty', caseParty);
  },
  openAddCasePartyRepresentative({ commit, state }, obj) {
    commit('updateSubMode', 'add');

    const relatedCaseParty = getCasePartyOrRepresentativeByObjectKey(
      state.lawsuitRequest.caseParties,
      obj.casePartyObjectKey
    );

    if (!isProsecutorOrDefendant(relatedCaseParty)) {
      const relatedObject = getRelatedCasePartyByObjectKey(
        relatedCaseParty,
        obj.casePartyObjectKey
      );
      relatedCaseParty.objectKey = relatedObject.objectKey;
      relatedCaseParty.casePartyRole = relatedObject.casePartyRole;
    }

    const casePartyRepresentative = initialData().selectedCaseParty;
    casePartyRepresentative.partyType = obj.casePartyRepresentativePartyType ?? partyTypeEnum.person;
    casePartyRepresentative.casePartyObjectKey = relatedCaseParty.objectKey;
    casePartyRepresentative.relatedPartiesInfo = [
      convertCasePartyToCasePartyRepresentative(
        +obj.casePartyRepresentativeRole,
        relatedCaseParty
      ),
    ];
    resetScroll();
    commit('updateSelectedCasePartyRepresentative', casePartyRepresentative);
  },
  openAddExistCasePartyAsRepresentative({ commit, state }, obj) {
    commit('updateSubMode', 'add');

    const relatedCaseParty = getCasePartyOrRepresentativeByObjectKey(
      state.lawsuitRequest.caseParties,
      obj.casePartyObjectKey
    );

    if (!isProsecutorOrDefendant(relatedCaseParty)) {
      const relatedObject = getRelatedCasePartyByObjectKey(
        relatedCaseParty,
        obj.casePartyObjectKey
      );
      relatedCaseParty.objectKey = relatedObject.objectKey;
      relatedCaseParty.casePartyRole = relatedObject.casePartyRole;
    }

    let casePartyRepresentative = getCasePartyOrRepresentativeByObjectKey(
      state.lawsuitRequest.caseParties,
      obj.casePartyRepresentativeObjectKey
    );
    if (!casePartyRepresentative) {
      casePartyRepresentative = initialData().selectedCaseParty;
      casePartyRepresentative.partyType = obj.casePartyRepresentativePartyType ?? partyTypeEnum.person;
    }
    casePartyRepresentative.casePartyObjectKey = relatedCaseParty.objectKey;
    casePartyRepresentative.relatedPartiesInfo.push(
      convertCasePartyToCasePartyRepresentative(
        +obj.casePartyRepresentativeRole,
        relatedCaseParty
      )
    );
    resetScroll();
    commit('updateSelectedCasePartyRepresentative', casePartyRepresentative);
  },
  openUpdateCaseParty({ commit, state }, obj) {
    commit('updateSubMode', 'edit');
    const caseParty = getCasePartyByObjectKey(
      state.lawsuitRequest.caseParties,
      obj.objectKey
    );
    caseParty.casePartyObjectKey = null;

    // fix scroll behavior
    resetScroll();

    commit('updateSelectedCaseParty', caseParty);
  },
  openUpdateCasePartyRepresentative({ commit, state }, obj) {
    commit('updateSubMode', 'edit');
    const casePartyRepresentative = getRepresentativeByRelatedObjectKey(
      state.lawsuitRequest.caseParties,
      obj.objectKey
    );

    // fix scroll behavior
    resetScroll();

    commit('updateSelectedCasePartyRepresentative', casePartyRepresentative);
  },
  openRemoveCaseParty({ commit, state }, obj) {
    const caseParty = getCasePartyByObjectKey(
      state.lawsuitRequest.caseParties,
      obj.objectKey
    );

    return services
      .validateRemovingCaseParty(caseParty)
      .then(() => {
        commit('updateSelectedCaseParty', caseParty);
      })
      .catch((msg) => {
        Vue.toaster.error(msg);
        throw msg;
      });
  },
  openRemoveCasePartyRepresentative({ commit, state }, obj) {
    const casePartyRepresentative = getRepresentativeByRelatedObjectKey(
      state.lawsuitRequest.caseParties,
      obj.objectKey
    );

    return services
      .validateRemovingCasePartyRepresentative(casePartyRepresentative)
      .then(() => {
        commit(
          'updateSelectedCasePartyRepresentative',
          casePartyRepresentative
        );
      })
      .catch((msg) => {
        Vue.toaster.error(msg);
        throw msg;
      });
  },
  saveCurrentUserAsOneOfCaseParties({ commit, getters }, caseParties) {
    services
      .validateCaseParties(caseParties)
      .then(() => {
        if (getters.isNewMode) {
          commit('saveNewCaseParties', caseParties);
          Vue.toaster.success('تمت الإضافة إلى صحيفة الدعوى بنجاح');
        } else {
          /*
          -Don't remove condition (refer to Ahmed Essawy)
          -Backend doesn't support adding more than one case party at once in edit mode
          */
          Vue.toaster.error('لم تتم الإضافة إلى صحيفة الدعوى.');
        }
      })
      .catch(Vue.toaster.error);
  },

  saveCasePartyInNewMode({ commit, state, getters }) {
    services
      .validateCaseParty(state.selectedCaseParty)
      .then(async () => {
        validateCasePartiesRepeatition(state, commit, 'add');
        if (getters.isAddSubMode) {
          if (isCasePartyExists(getters.selectedCasePartyId, getters.casePartyIds)) {
            commit('saveExistCasePartyRepresentativeAsCaseParty', state.selectedCaseParty);
          } else {
            await saveHeirsList(getters, state);
            commit('saveNewCaseParty', state.selectedCaseParty);
          }
          Vue.toaster.success('تم إضافة طرف الدعوى إلى صحيفة الدعوى بنجاح');
        } else if (getters.isEditSubMode) {
          await saveHeirsList(getters, state);
          commit('saveExistCaseParty', state.selectedCaseParty);
          Vue.toaster.success('تم تعديل بيانات طرف الدعوى بنجاح');
        }
        commit('resetCasePartyForm');
      })
      .catch(Vue.toaster.error);
  },

  saveCasePartyRepresentativeInNewMode({ commit, state, getters }) {
    services
      .validateCasePartyRepresentative(state.selectedCaseParty, state.lawsuitRequest.caseParties)
      .then(() => {
        // if the selected representative is inheritor of any defendant, remove it from the case parties list
        const defendants = state.lawsuitRequest.caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.defendant);
        const inheritorsOfDefendants = state.lawsuitRequest.caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.inheritor &&
          defendants.map(d => d.socialId).includes(cp.inheritorSocialId));
        var existAsInheritor = inheritorsOfDefendants.find(cp => cp.socialId === state.selectedCaseParty.socialId);
        if (existAsInheritor) {
          //delete Inheritor then add case party again
          const index = state.lawsuitRequest.caseParties.indexOf(existAsInheritor);
          if (index > -1) {
            state.lawsuitRequest.caseParties.splice(index, 1);
          }
        }
        if (getters.isAddSubMode) {
          if (
            isCasePartyExists(getters.selectedCasePartyId, getters.casePartyIds)
          ) {
            commit(
              'saveExistCasePartyAsRepresentative',
              state.selectedCaseParty
            );
          } else {
            commit('saveNewCasePartyRepresentative', state.selectedCaseParty);
          }
          Vue.toaster.success(
            'تم إضافة الممثل إلى قائمة ممثلي المدعي/المدعى عليه بنجاح'
          );
        } else if (getters.isEditSubMode) {
          commit('saveExistCasePartyRepresentative', state.selectedCaseParty);
          Vue.toaster.success('تم تعديل بيانات الممثل بنجاح');
        }
        commit('resetCasePartyForm');
      })
      .catch(Vue.toaster.error);
  },
  saveCasePartyInEditMode({ commit, state, getters }) {
    services
      .validateCasePartyRepresentative(state.selectedCaseParty)
      .then(() => {
        validateCasePartiesRepeatition(state, commit, 'edit');
        if (getters.isAddSubMode) {
          api
            .addCaseParty(getters.requestObjectKey, getters.selectedCaseParty)
            .then(async (res) => {
              await saveHeirsList(getters, state);
              commit('updateSelectedCasePartyObjectKey', res.data);
              if (isCasePartyExists(getters.selectedCasePartyId, getters.casePartyIds)) {
                commit('saveExistCasePartyRepresentativeAsCaseParty', state.selectedCaseParty);
              } else {
                commit('saveNewCaseParty', state.selectedCaseParty);
              }
              Vue.toaster.success('تم إضافة طرف الدعوى إلى صحيفة الدعوى بنجاح');
              commit('resetCasePartyForm');
            });
        } else if (getters.isEditSubMode) {
          api
            .updateCaseParty(
              getters.requestObjectKey,
              getters.selectedCaseParty
            )
            .then(async (res) => {
              await saveHeirsList(getters, state);
              commit('saveExistCaseParty', state.selectedCaseParty);
              Vue.toaster.success('تم تعديل بيانات طرف الدعوى بنجاح');
              commit('resetCasePartyForm');
            });
        }
      })
      .catch(Vue.toaster.error);
  },
  saveCasePartyRepresentativeInEditMode({ commit, state, getters }) {
    services
      .validateCasePartyRepresentative(state.selectedCaseParty)
      .then(() => {
        const defendants = state.lawsuitRequest.caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.defendant);
        const inheritorsOfDefendants = state.lawsuitRequest.caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.inheritor &&
          defendants.map(d => d.socialId).includes(cp.inheritorSocialId));
        var existAsInheritor = inheritorsOfDefendants.find(cp => cp.socialId === state.selectedCaseParty.socialId);
        if (existAsInheritor) {
          //delete Inheritor then add case party again
          commit('removeCaseParty', { objectKey: existAsInheritor.objectKey });
        }
        if (getters.isAddSubMode) {
          api.addCaseParty(getters.requestObjectKey, getters.selectedCaseParty)
            .then((res) => {
              commit(
                'updateSelectedCasePartyRepresentativeObjectKey',
                res.data
              );
              if (
                isCasePartyExists(
                  getters.selectedCasePartyId,
                  getters.casePartyIds
                )
              ) {
                commit(
                  'saveExistCasePartyAsRepresentative',
                  state.selectedCaseParty
                );
              } else {
                commit(
                  'saveNewCasePartyRepresentative',
                  state.selectedCaseParty
                );
              }
              Vue.toaster.success(
                'تم إضافة الممثل إلى قائمة ممثلي المدعي/المدعى عليه بنجاح'
              );
              commit('resetCasePartyForm');
            });
        } else if (getters.isEditSubMode) {
          const currentRelatedCaseParty = (
            getters.selectedCaseParty.relatedPartiesInfo || []
          ).find(
            rp =>
              rp.casePartyObjectKey ===
              getters.selectedCaseParty.casePartyObjectKey
          );
          const selectedCaseParty = { ...getters.selectedCaseParty };
          if (currentRelatedCaseParty) {
            selectedCaseParty.objectKey = currentRelatedCaseParty.objectKey;
          }
          api
            .updateCaseParty(getters.requestObjectKey, selectedCaseParty)
            .then(() => {
              commit(
                'saveExistCasePartyRepresentative',
                state.selectedCaseParty
              );
              Vue.toaster.success('تم تعديل بيانات الممثل');
              commit('resetCasePartyForm');
            });
        }
      })
      .catch(Vue.toaster.error);
  },
  removeCaseParty({ commit, state, getters }) {
    const objectKey = state.selectedCaseParty.objectKey;

    if (getters.isNewMode) {
      commit('removeCaseParty', { objectKey });
      Vue.toaster.success('تم حذف طرف الدعوى من صحيفة الدعوى بنجاح');
      return Promise.resolve();
    } else if (getters.isEditMode) {
      return api
        .removeCaseParty(getters.requestObjectKey, objectKey)
        .then((res) => {
          commit('removeCaseParty', { objectKey });
          Vue.toaster.success('تم حذف طرف الدعوى من صحيفة الدعوى بنجاح');
          return res;
        });
    }
    return Promise.resolve();
  },
  removeCasePartyRepresentative({ commit, state, getters }) {
    const representativeObjectKey = getRepresentativeObjectKeyByRelatedObjectKey(
      state.selectedCaseParty,
      state.selectedCaseParty.casePartyObjectKey
    );

    if (getters.isNewMode) {
      commit('removeCasePartyRepresentative', { objectKey: representativeObjectKey });
      Vue.toaster.success(
        'تم حذف الممثل من قائمة ممثلي المدعي/المدعى عليه بنجاح'
      );
      return Promise.resolve();
    } else if (getters.isEditMode) {
      return api
        .removeCaseParty(getters.requestObjectKey, representativeObjectKey)
        .then((res) => {
          commit('removeCasePartyRepresentative', { objectKey: representativeObjectKey });
          Vue.toaster.success(
            'تم حذف الممثل من قائمة ممثلي المدعي/المدعى عليه بنجاح'
          );
          return res;
        });
    }
  },
  cancelCasePartyForm({ commit }) {
    commit('resetCasePartyForm');
  },
  removeCasePartyCanceled({ commit }) {
    commit('resetCasePartyForm');
  },
  uploadBase64Attachment({ getters }, payload) {
    return api.uploadAttachment(getters.requestObjectKey, payload);
  },
  uploadAttachment: async ({ commit, getters }, payload) => {
    // payload: {
    //   lawsuitObjectKey: this.objectKey,
    // attachmentKey: [this.selectedAttachmentKey],
    // attachment: Object.assign({}, this.data, {
    //   name,
    //   category,
    //   url,
    //   fileObject,
    //   type,
    // }),
    // }

    const {
      name,
      fileType,
      fileUrl,
      category,
      categoryName,
      fileObject,
      extension,
    } = payload.attachment;

    // if (
    //   (
    //     category === attachmentCategory.residence ||
    //   category === attachmentCategory.prosecutorIdentity ||
    //   category === attachmentCategory.respondentIdentity ||
    //   category === attachmentCategory.agentIdentity ||
    //   category === attachmentCategory.attorneyIdentity
    //   ) && (
    //     fileType === attachmentType.image
    //   )
    // ) {
    //   const data = new FormData();
    //   data.set('images', fileObject);
    //   const res = await api.detectAttachment(data);
    //   const { response } = res.data;

    //   if (response.length <= 0 || !isAttachmentDetected(category, response)) {
    //     vue.toaster.error('الملف الذي قمت برفعه لا يتناسب مع نوع الملف الذي قمت باختياره.');
    //     return Promise.reject();
    //   }
    // }

    return api
      .uploadAttachment(getters.requestObjectKey, {
        fileName: name,
        fileType: fileType,
        content: fileUrl.split(',')[1],
        fileCategory: category,
        extension: extension,
      })
      .then((res) => {
        commit('updateAttachments', {
          [payload.attachmentKey]: {
            name,
            category,
            categoryName,
            fileUrl,
            fileObject,
            fileType,
            objectKey: res.data,
          },
        });
      });
  },
  deleteAttachment({ commit, getters }, payload) {
    // payload: {
    //   objectKey: string,
    //   attachmentKey,
    //   attachment: {
    //      "name": "string",
    //      "type": 0,
    //      "url": "string",
    //      "fileObject": FileObject,
    //      "category": 0,
    //    }
    // }

    const { objectKey, attachmentKey } = payload;
    return api
      .deleteAttachment(getters.requestObjectKey, objectKey)
      .then(() => {
        commit('deleteAttachment', attachmentKey);
        commit('updateDeleteAttachmentModalVisibility', { visibility: false });
      });
  },
  getLawsuitCaseAttachment({ commit, getters }) {
    api.getLawsuitCaseAttachment(getters.requestObjectKey)
      .then((res) => {
        const attachments = res.data.list.reduce((attachmentsObj, attachment) => {
          // eslint-disable-next-line
          attachmentsObj[attachment.objectKey] = {
            name: attachment.fileName,
            category: attachment.attachmentTypeId, // category
            categoryName: attachment.attachmentTypesName,
            fileUrl: null,
            fileObject: null,
            fileType: attachment.fileType,
            objectKey: attachment.objectKey,
            description: attachment.description,
            acceptanceCriteria: attachment.acceptanceCriteria,
            sampleImageDownloadBase64: attachment.sampleImageDownloadBase64,
            isAttachmentAudited: attachment.isAttachmentAudited,
            isAttachmentVisible: attachment.isAttachmentVisible,
          };
          return attachmentsObj;
        }, {});
        commit('setOriginalData', { attachments });
        commit('setAttachments', attachments);
      });
  },
  getAttachmentURL({ commit, state, getters }, attachmentObjectKey) {
    return api
      .getAttachmentURL(getters.requestObjectKey, attachmentObjectKey)
      .then((res) => {
        commit('updateAttachments', {
          [attachmentObjectKey]: Object.assign(
            {},
            state.attachments.data[attachmentObjectKey],
            { fileUrl: res.data }
          ),
        });
        commit('updateAttachmentModal', {
          selectedAttachment: {
            ...state.attachments.viewAttachmentModal.selectedAttachment,
            fileUrl: res.data,
          },
        });
        return res.data;
      });
  },
  checkCaseInfoByCaseCode({ commit, state }, payload) {
    const caseData = state.lawsuitRequest.case;
    return api.checkCaseInfoByCaseCode(payload)
      .then((res) => {
        if (res.data.courtId == state.lawsuitRequest.case.courtId) {
          let relatedCases = [res.data];
          commit('setRelatedCase', getDistinctItemsByKeys(relatedCases, ['caseCode', 'caseLifeTimeId']));

          const relatedCaseFormValue = [{
            ...payload,
            caseLifeTimeId: res.data.caseLifeTimeId,
          }];

          caseData.relatedCaseFormValue = getDistinctItemsByKeys(relatedCaseFormValue, ['caseCode', 'caseYear']);
          caseData.caseHasChanges = true;
          commit('updateCase', { ...caseData });
          Vue.toaster.success(ar.common.verificationSucceeded);
          return true;
        } else
          Vue.toaster.error(ar.lawsuitDetails.similarCase.courtNotFound);
        return false;
      })
      .catch(() => {
        Vue.toaster.error(ar.lawsuitDetails.similarCase.caseNotFound);
        return false;
      });
  },
  getJudgementDocument(context, payload) {
    return new Promise((resolve, reject) => {
      api.getJudgementDocumentByCaseCodeAndCaseYear(payload)
        .then((document) => {
          processJudgementDocument({
            resolve,
            reject,
            document,
            context,
          });
        })
        .catch(() => {
          reject(ar.common.noData);
        });
    });
  },
  getJudgementDocumentByCodeCaseCodePartyIdentity(context, payload) {
    return new Promise((resolve, reject) => {
      api.getJudgementDocumentByCodeCaseCodePartyIdentity(payload)
        .then((document) => {
          processJudgementDocument({
            resolve,
            reject,
            document,
            context,
          });
        })
        .catch(() => {
          reject(ar.common.noData);
        });
    });
  },
  getCaseMovementReplyTypes({ commit, getters }) {
    return api
      .getCaseMovementReplyTypes(getters.requestObjectKey)
      .then((res) => {
        commit('updateCaseMovementReplyTypes', res.data);
        return res.data;
      });
  },
  handleDeleteAttachmentsByCategory(context, fileCategory) {
    return new Promise((resolve) => {
      const requestObjectKey = context.rootGetters['LawsuitModule/requestObjectKey'];
      const lawsuitState = context.rootState['LawsuitModule'];
      const filteredContractFiles = Object.values(lawsuitState.attachments.data)
        .filter(c => c.category === fileCategory);

      if (requestObjectKey) {
        if (filteredContractFiles && filteredContractFiles.length > 0) {
          filteredContractFiles.forEach((filteredContract) => {
            context.dispatch('LawsuitModule/deleteAttachment', {
              objectKey: filteredContract.objectKey,
              attachmentKey: filteredContract.objectKey,
            }, { root: true });
          });
          context.commit('LawsuitModule/setAttachments',
            [...Object.values(lawsuitState.attachments.data)
              .filter(c => c.category !== fileCategory)], { root: true });
        }
        resolve(false);
      } else {
        if (fileCategory) {
          context.commit('LawsuitModule/updateLawsuitRequestAttachments',
            [...lawsuitState.lawsuitRequest.requestAttachments.
              filter(a => a.fileCategory !== fileCategory)], { root: true });
          resolve(true);
        } else {
          resolve(false);
        }
      }
    });
  },
  verifyJudicialInformingDecision(context, payload) {
    return new Promise((resolve, reject) => {
      api.getJudicialInformingDecision(payload)
        .then((res) => {
          if (res && res.status == 200) {
            resolve({ res });
          }
          else
            reject(ar.common.noData);
        })
        .catch(() => {
          reject(ar.common.noData);
        });
    });
  },
  submitCaseReply({ state, getters }, payload) {
    return new Promise((resolve, reject) => {
      services
        .validateLawsuitCaseSubject({
          ...state.lawsuitRequest,
          requestInfo: state.submittedRequestInfo,
        }, true)
        .then(() => {
          const bypassReplyReasons = [
            requestReplyTypes.Rejected.id,
            requestReplyTypes.NotCompleted.id,
            requestReplyTypes.CancelRequest.id,
            requestReplyTypes.CancelRequestByApplicant.id,
          ];
          services
            .validateCaseSubmission()
            .then(() => {

              const bypassValidation = bypassReplyReasons.some(
                r => r === payload.requestReply
              );
              const requestObjectKey = getters.requestObjectKey || payload.requestObjectKey;

              services
                .validateLawsuit(bypassValidation)
                .then(() => {
                  api
                    .submitCaseReply(requestObjectKey, payload)
                    .then((res) => {
                      if (!payload.disableRedirect) {
                        router.push({ path: `/lawsuit-requests/${requestObjectKey}/review-ticket/print` });
                      }
                      resolve(res);
                    })
                    .catch((msg) => {
                      !msg.handled && Vue.toaster.error(msg);
                      reject(msg);
                    });
                })
                .catch((msg) => {
                  !msg.handled && Vue.toaster.error(msg);
                  reject(msg);
                });
            }).catch((msg) => {
              !msg.handled && Vue.toaster.error(msg);
              reject(msg);
            });
        })
        .catch((msg) => {
          !msg.handled && Vue.toaster.error(msg);
          reject(msg);
        });
    });
  },
  submitLawsuitRedistribution(context, payload) {
    return api.submitLawsuitRedistribution(payload).then(res => res);
  },
  submitServicesCenterRedistribution(context, payload) {
    return api.submitServicesCenterRedistribution(payload).then(res => res);
  },
  changeAuditPaths(context, payload) {
    return api.changeAuditPaths(payload).then(res => res);
  },
  updatePartyAuthentication(context, payload) {
    return api.updatePartyAuthentication(payload).then(res => res.data);
  },
  submitCentralAuditRedistribution(context, payload) {
    return api.submitCentralAuditRedistribution(payload).then(res => res);
  },
  openPrintTicketPage(context, payload) {
    return api.getDistributionTicket(payload)
      .then(res => res.data);
  },
  getPartyExecutionRequests(_context, payload) {
    return api.getPartyExecutionRequests(payload);
  },
  getSimilarCases({ commit, getters }, payload) {
    return api
      .getSimilarCases({
        ...payload,
        requestObjectKey: getters.requestObjectKey,
      })
      .then((res) => {
        commit('updateSimilarCases', res.data);
        return res.data;
      });
  },
  getSimilarReconciliationRequests({ commit, getters }, payload) {
    return api
      .getSimilarReconciliationRequests({
        ...payload,
        requestObjectKey: getters.requestObjectKey,
      })
      .then((res) => {
        commit('updateSimilarReconciliationRequests', res.data);
        return res.data;
      });
  },
  getRelatedCases({ commit, getters }, payload) {
    return api
      .getRelatedCases({
        ...payload,
        requestObjectKey: getters.requestObjectKey,
      })
      .then((res) => {
        commit('updateRelatedCases', res.data);
        return res.data;
      });
  },
  addRelatedCases({ getters }, payload) {
    // payload: {
    // "requestObjectKey": "string",
    // requestBody: {
    // "relatedCaseId": 0,
    // "relatedCaseCode": 0,
    // }
    // }
    return api
      .addRelatedCases({
        ...payload,
        requestObjectKey: getters.requestObjectKey,
      })
      .then(res => res);
  },
  removeRelatedCases({ getters }, relatedCaseObjectKey) {
    return api
      .removeRelatedCases({
        relatedCaseObjectKey,
        requestObjectKey: getters.requestObjectKey,
      })
      .then(res => res);
  },
  getLawsuitCaseReport(context, payload) {
    return api.getLawsuitCaseReport(payload)
      .then(res => res.data);
  },
  getPortalLawsuitReport(context, payload) {
    return api.getPortalLawsuitReport(payload)
      .then(res => res.data);
  },
  getCasePreviousReplies({ commit }, payload) {
    let apiMethod = 'getPortalCasePreviousReplies';
    if (payload.isCourtsRequestPreviousReplies)
      apiMethod = 'getCasePreviousReplies';
    return api[apiMethod](payload.objectKey)
      .then((res) => {
        commit('updateCasePreviousReplies', res.data);
        return res;
      });
  },
  resetCasePreviousReplies({ commit }) {
    commit('updateCasePreviousReplies', []);
  },
  updateSimilarCasesModalVisibility({ commit }, payload) {
    commit('updateSimilarCasesModalVisibility', { visibility: payload.visibility });
  },
  updateReplyModalVisibility({ commit }, payload) {
    commit('updateReplyModalVisibility', { visibility: payload.visibility });
  },
  updateAuditFields({ getters, commit }, payload) {
    if (getters.isEditMode) {
      return api.updateAuditFields({
        payload,
        requestObjectKey: getters.requestObjectKey,
      })
        .then(() => { commit('updateAuditFields', payload); });
    }
  },
  getAuditFields({ getters, commit }) {
    return api.getAuditFields(getters.requestObjectKey)
      .then((res) => {
        commit('updateAuditFields', res.data);
        return res.data;
      });
  },
  addCaseTypeDynamicFormModel({ commit, state }, dynamicFormModel) {

    commit(
      'updateLawsuitDynamicFormsModel',
      state.lawsuitRequest.case.forms.concat(dynamicFormModel)
    );
  },
  removeCaseTypeDynamicFormModel({ commit, state }, formName) {

    commit(
      'updateLawsuitDynamicFormsModel',
      state.lawsuitRequest.case.forms.filter(form => form.name !== formName)
    );
  },
  resetCaseTypeDynamicFormModel({ commit }) {
    commit('updateLawsuitDynamicFormsModel', []);
  },
  updateLawsuitDynamicForms({ commit }, dynamicForm) {
    commit('updateLawsuitDynamicForms', { [dynamicForm.name]: dynamicForm });
  },
  getReviewTicket(store, payload) {
    return api.getReviewTicket(payload)
      .then((res) => {
        return {
          success: true,
          data: res.data,
        };
      })
      .catch((err) => {
        const status = (err.response || {}).status;
        const errors = ((err.response || {}).data || {}).errors;
        // check if it's a 404 error or a 400 error with invalid GUID
        if (status === 404 || (status === 400 && errors && errors.objectkey)) {
          router.push('/error/404');
        }
        return {
          success: false,
          message: err.message,
        };
      });
  },
  getRequiredAttachments(store, payload) {
    return api.getRequiredAttachments(payload);
  },
  async getRequiredAttachmentsByRequestObjectKey({ commit }, payload) {
    const res = await api.getRequiredAttachmentsByRequestObjectKey(payload);
    commit('updateLawsuitRequiredAttachmentTypes', res.data);
    return res.data;
  },
  async getAttachmentTypesByRequestObjectKey({ commit }, payload) {
    const res = await api.getAttachmentTypesByRequestObjectKey(payload);
    commit('updateLawsuitAttachmentTypes', res.data);
    return res.data;
  },
  async updateRequestAttachmentAuditField({ commit }, payload) {
    const res = await api.updateRequestAttachmentAuditField(payload);
    commit('updateRequestAttachmentAuditField', payload);
    return res.data;
  },
  async getPartiesClosestCourts(store, payload) {
    return api.getPartiesClosestCourts(payload);
  },
  getExecutionCourts(store, payload) {
    return api.getExecutionCourts(payload);
  },
  searchCasesTypes: takeLatest((_store, payload) => api.searchCasesTypes(payload)),
  async getSimilarRequests({ commit, getters }, payload) {
    const res = await api
      .getSimilarRequests({
        ...payload,
        requestObjectKey: getters.requestObjectKey,
      });
    commit('updateSimilarRequests', res.data);
    return res.data;
  },
  updateIsPledged({ commit }, payload) {
    commit('updateIsPledged', payload);
  },
  getUpcomingSessions(store) {
    return api.getUpcomingSessions();
  },
  getSessionsRange(store, payload) {
    return api.getSessionsRange({
      dateFrom: payload.dateFrom,
      dateTo: payload.dateTo,
    });
  },
  getMyRequests(store, payload) {
    return api.getMyRequests(payload);
  },
  getMyLegacyCaseDigitizationRequest(store, payload) {
    return api.getMyLegacyCaseDigitizationRequest(payload);
  },
  getRequestsFeesEstimation(store, payload) {
    return api.getRequestsFeesEstimation(payload);
  },
  addCasesDigitizationRequest(context, payload) {
    return api.addCasesDigitizationRequest(payload);
  },
  getArbitrationAward(context, payload) {
    return api.getArbitrationAward(payload)
      .then((res) => {
        return res;
      });
  },
  getJudgementSections(_context, params) {
    return api.getJudgementSections(params).then(res => res.data);
  },
  checkMarriageDeeds(context, payload) {
    return new Promise((resolve, reject) => {
      api.checkMarriageDeeds(payload)
        .then((res) => {
          if (res && res.status == 200) {
            resolve(res.data);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  resetCaseCourtId({ commit }) {
    commit('resetCaseCourtId');
  },
  resetRelatedCases({ commit, state }) {
    commit('setRelatedCase', []);
    const caseData = state.lawsuitRequest.case;
    caseData.relatedCaseFormValue = [];
    caseData.caseHasChanges = true;
    commit('updateCase', { ...caseData });
  },
  checkPartyAuthentication(context, payload) {
    return new Promise((resolve, reject) => {
      api.checkPartyAuthentication(payload)
        .then((res) => {
          if (res && res.status === 200) {
            resolve(res.data);
          } else {
            reject(new Error('Invalid response from server'));
          }
        })
        .catch((err) => {
          console.error('Error in checkPartyAuthentication:', err);
          reject(err);
        });
    });
  },
  getJudgementDeeds(_context, payload) {
    return api.getJudgementDeeds(payload);
  },
  // صفات الاطراف
  async getActivePartyAttributes({ commit }, { caseTypeId, partyTypeId, partyRoleId, casePartyRole }) {
    commit('updateActivePartyAttributes', []);
    const { data } = await api.getActivePartyAttributes({
      caseTypeId,
      legalPositionId: casePartyRole,
      partyTypeId,
      partyRoleId,
    });
    commit('updateActivePartyAttributes', data);
    return data;
  },
  resetCaseParties({ commit }) {
    commit('resetCaseParties');
  },
  resetCasePartiesByObjectKeys({ commit }, objectKeys) {
    commit('resetCasePartiesByObjectKeys', objectKeys);
  },
  async getPartyAttributesBasicInfo({ commit }) {
    commit('setPartyAttributesBasicInfo', []);
    const { data } = await api.getPartyAttributesBasicInfo();
    commit('setPartyAttributesBasicInfo', data);
    return data;
  },
  async deleteCasePartiesByObjectKeys({ commit, state, getters }, objectKeys) {
    return api.deleteCasePartiesByObjectKeys(getters.requestObjectKey, objectKeys).then(() => {
      commit('resetCasePartiesByObjectKeys', objectKeys);
    });
  },
  getIncomingRequests(store, payload) {
    return api.getIncomingRequests(payload);
  },
  userHasIncomingRequests(store, payload) {
    return api.userHasIncomingRequests(payload);
  },
  getRequestSimpleInfo(store, objectKey) {
    return api.getLawsuitRequestInfo(objectKey);
  },
});

const getLawsuitCaseInfo = ({ commit }, objectKey) => {
  api.getLawsuitCase(objectKey)
    .then((res) => {
      return services
        .refineLawsuitCase(res.data)
        .then((caseData) => {
          commit('setOriginalData', caseData);
          commit('updateCase', caseData);
          return caseData;
        })
        .catch(() => { });
    });
};

const getDistinctItemsByKeys = (array, keys) => {
  return array.filter((item, index, self) =>
    index === self.findIndex(t =>
      keys.every(key => t[key] === item[key])
    )
  );
};

const saveHeirsList = async (getters, state) => {
  if ((state.selectedCaseParty.heirsList || []).length > 0) {
    const selectedCaseParty = state.selectedCaseParty;
    const filteredHeirsList = selectedCaseParty.heirsList.filter((heir) => {
      return !state.lawsuitRequest.caseParties.some((cp) => {
        const samePerson = cp.socialId === heir.socialId;
        const isMainRole =
          cp.casePartyRole === casePartyRoleEnum.prosecutor ||
          cp.casePartyRole === casePartyRoleEnum.defendant;

        const sameInheritor =
          cp.socialId === heir.socialId &&
          heir.inheritorSocialId === cp.inheritorSocialId;

        return (samePerson && isMainRole) || (sameInheritor && !isMainRole);
      });
    });
    const mappedHeirsList = filteredHeirsList.map((element) => {
      return {
        ...element,
        casePartyRole: casePartyRoleEnum.inheritor,
        socialIdType: element.socialIdTypeId,
        socialIdTypeId: element.socialIdTypeId,
        partyType: selectedCaseParty.partyType,
        relatedDeceasedSocialId: selectedCaseParty.socialId,
        relatedPartiesInfo: [],
        socialId: element.socialId,
        dateOfBirth: element.dateOfBirth,
        contact: {
          email: null,
          fax: null,
          homePhone: null,
          mobile: null,
          workPhone: null,
        },
      };
    });

    // process all heirs with MOI checks
    const moiChecks = mappedHeirsList.map(async (element) => {
      const personalInfoRequest = {
        socialId: element.socialId,
        dateOfBirth: element.dateOfBirth,
      };

      try {
        const personalInfo = await Store.dispatch('SystemModule/checkMOIPersonalInfo', personalInfoRequest);
        if (personalInfo.isRetrievedFromMoi) {
          Object.assign(element, personalInfo);

          if (element.socialIdTypeId == socialIdentityTypeEnum.nationalId || element.socialIdTypeId == socialIdentityTypeEnum.regularResidency) {
            Store.dispatch('SystemModule/checkPersonalAddress', element.socialId)
              .then((address) => {
                element.nationalAddress = {
                  ...element.nationalAddress,
                  ...address.nationalAddresses[0],
                };
                element.nationalAddresses = address.nationalAddresses;
              });
          }
          else {
            element.residenceAddress = { countryId: countriesEnum.saudiArabia };
          }
          // add the case party
          state.lawsuitRequest.caseParties.push(element);
        }
      } catch (error) {
        element.isRetrievedFromMoi = false;
      }
      return element;
    });

    if (getters.isEditMode) {
      // wait for all MOI checks to complete
      await Promise.all(moiChecks);

      // after all MOI checks are done, add multiple case parties
      await api.addMultipleCaseParties(getters.requestObjectKey, mappedHeirsList);
    }
  }
};

const validateCasePartiesRepeatition = (state, commit, mode) => {
  // check if the selected case party exist as inheritor and remove it
  var existAsInheritor = state.lawsuitRequest.caseParties.find(cp => cp.socialId === state.selectedCaseParty.socialId &&
    cp.casePartyRole === casePartyRoleEnum.inheritor);
  if (existAsInheritor) {
    //delete Inheritor then add case party again
    if (mode === 'edit') {
      commit('removeCaseParty', { objectKey: existAsInheritor.objectKey });
    }
    else if (mode === 'add') {
      const index = state.lawsuitRequest.caseParties.indexOf(existAsInheritor);
      if (index > -1) {
        state.lawsuitRequest.caseParties.splice(index, 1);
      }
    }
  }
  // فلترة الورثة حسب الشروط قبل الحفظ
  if (state.selectedCaseParty.heirsList && state.selectedCaseParty.heirsList.length > 0) {
    const caseParties = state.lawsuitRequest.caseParties;
    const prosecutors = caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.prosecutor);
    const defendants = caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.defendant);
    const allInheritorOfDefendants = caseParties.filter(cp => cp.casePartyRole === casePartyRoleEnum.inheritor);

    // فلترة الورثة
    state.selectedCaseParty.heirsList = state.selectedCaseParty.heirsList.filter((heir) => {
      // هل الوريث موجود كمدعى عليه أصيل؟
      const isDefendant = defendants.some(cp => cp.socialId === heir.socialId && cp.casePartyRole === casePartyRoleEnum.defendant);
      // هل الوريث موجود كمدعي أصيل؟
      const isProsecutor = prosecutors.some(cp => cp.socialId === heir.socialId && cp.casePartyRole === casePartyRoleEnum.prosecutor);
      // إذا كان الوريث موجود كمدعى عليه أو كمدعي أصيل => لا تضفه
      if (isDefendant || isProsecutor) return false;

      //هل الوريث موجود كوريث لمدعى او مدعى عليه اخر
      const isInheritor = allInheritorOfDefendants.some(cp => cp.socialId === heir.socialId);
      if (isInheritor) return false;

      // هل الوريث موجود كممثل لأحد الأطراف الأصيلة (مدعي )؟
      const isRepresentative = caseParties.some(cp => cp.relatedPartiesInfo.length > 0 && cp.socialId === heir.socialId);
      if (isRepresentative && state.selectedCaseParty.casePartyRole === casePartyRoleEnum.defendant)
        return false;

      // إذا كان موجود كمدعي ثانوي => يمكن تكراره
      return true;
    });
  }

};
const processJudgementDocument = ({ resolve, reject, document, context }) => {
  const promises = [];
  const attachments = [];

  const requestObjectKey = context.rootGetters['LawsuitModule/requestObjectKey'];
  const lawsuitState = context.rootState['LawsuitModule'];
  const filteredJudgementFiles = Object.values(lawsuitState.attachments.data)
    .filter(c => c.category === attachmentCategory.judgementImage);
  if (requestObjectKey) {
    if (filteredJudgementFiles && filteredJudgementFiles.length > 0)
      filteredJudgementFiles.forEach((filteredJudgement) => {
        context.dispatch('LawsuitModule/deleteAttachment', {
          objectKey: filteredJudgement.objectKey,
          attachmentKey: filteredJudgement.objectKey,
        }, { root: true });
      });
  }
  if (document && (document.data || []).length > 0) {
    const courtName = document.data[0].courtName || '';
    const subCircleName = document.data[0].subCircleName || '';
    document.data.forEach((judgement) => {
      const name = (judgement || {}).fileName ? judgement.fileName : ar.dynamicFormAdmin.customComponents.judgementDocument.judgementName;
      const category = attachmentCategory.judgementImage;
      const extension = (judgement || {}).fileName && judgement.fileName.includes('.') ? '.' + judgement.fileName.split('.')
        .pop()
        .toLowerCase() : '.pdf';
      const fileType = extension === '.pdf' ? attachmentType.pdf : attachmentType.image;
      let attachment = {
        fileName: name,
        fileType,
        content: (judgement || {}).spid,
        fileCategory: category,
        sharePointId: (judgement || {}).spid,
        extension,
        isAttachmentAudited: true,
        isAttachmentVisible: false,
      };
      attachments.push(attachment);
    });
    if (attachments.length > 0) {
      const attachmentArray = [];
      attachments.forEach((attachment) => {
        if (requestObjectKey) {
          if ((attachment || {}).sharePointId)
            promises.push(api.uploadAttachment(requestObjectKey, attachment));
          else
            resolve({
              status: ar.dynamicFormAdmin.customComponents.judgementDocument.verificationSucceededUploadFailed,
              courtName,
              subCircleName,
            });
        } else {
          attachmentArray.push(attachment);
          const otherAttachment = lawsuitState.lawsuitRequest.requestAttachments.filter(a => a.fileCategory !== attachment.fileCategory);
          if (otherAttachment.length > 0)
            context.commit('LawsuitModule/updateLawsuitRequestAttachments', [...attachmentArray, ...otherAttachment], { root: true });
          else
            context.commit('LawsuitModule/updateLawsuitRequestAttachments', attachmentArray, { root: true });
          resolve({
            status: ar.dynamicFormAdmin.customComponents.judgementDocument.verificationSucceededUploadSucceeded,
            courtName,
            subCircleName,
          });
        }
      });
    }
    if (promises.length > 0) {
      Promise.all(promises)
        .then((results) => {
          for (var i = 0; i < results.length; i++) {
            let attachmentData = {
              name: attachments[i].fileName,
              category: attachments[i].fileCategory,
              categoryName: attachments[i].categoryName,
              fileUrl: attachments[i].fileUrl,
              fileObject: attachments[i].fileObject,
              fileType: attachments[i].fileType,
              objectKey: results[i].data,
              isAttachmentAudited: true,
              isAttachmentVisible: false,
            };
            const attachmentType = lawsuitState.lawsuitRequest.attachmentTypes.find(a => a.id == attachments[i].fileCategory) || {};
            if (Object.hasOwnProperty.call(attachmentType, 'isRequired')) {
              attachmentData.isRequired = attachmentType.isRequired;
            }
            context.commit('LawsuitModule/updateAttachments', { [attachmentData.objectKey]: attachmentData }, { root: true });
            context.dispatch('LawsuitModule/updateRequestAttachmentAuditField', {
              requestAttachmentObjectKey: results[i].data,
              isAttachmentAudited: true,
              isAttachmentVisible: false,
            }, { root: true });
            resolve({
              status: ar.dynamicFormAdmin.customComponents.judgementDocument.verificationSucceededUploadSucceeded,
              courtName,
              subCircleName,
            });
          }
        });
    }
  }
  else
    reject(ar.common.noData);
};
